/**
 * Platform sub-page content (the "Platform" mega-menu topics).
 *
 * Mirrors the data-driven pattern in ./solutions.ts: one typed array drives routing,
 * SEO, the nav mega-menu, and the shared <PlatformTopicPage> template.
 *
 * Content is adapted from the platform feature overview and the already-vetted copy in
 * PlatformPage.tsx / solutions.ts. Per project rule: no fabricated metrics or certs —
 * capability-framed, or reuse existing anonymized reference outcomes. Never claim SOC 2.
 */

import {
  Compass,
  Bot,
  Users,
  Database,
  Plug,
  Boxes,
  MessageSquare,
  Gauge,
  Cpu,
  type LucideIcon,
} from "lucide-react";
import type { GraphicName } from "../components/PlatformGraphics";

export interface FeatureBlock {
  title: string;
  body: string;
  points?: string[];
}

export interface HowStep {
  title: string;
  desc: string;
}

export interface RelatedLink {
  page: string;
  label: string;
  desc: string;
}

export interface CardGroup {
  eyebrow: string;
  headline: string;
  sub?: string;
  items: { title: string; body: string }[];
}

export interface Comparison {
  vs: string;
  body: string;
  /** the one-line takeaway under the comparison */
  tag: string;
}

export interface Outcome {
  value: string;
  label: string;
  note: string;
}

export interface ConnectorGroup {
  tier: string;
  items: { name: string; desc: string }[];
}

export interface PackItem {
  name: string;
  status: "Live" | "In design";
  desc: string;
  /** solutions page for the pack, when one exists */
  page?: string;
}

export interface ModelRow {
  family: string;
  sizes: string;
  kind: string;
  /** benchmarked in our lab, or "—" */
  tested: string;
}

export interface PlatformTopic {
  /** route slug, e.g. "agents" → /platform/agents */
  slug: string;
  /** page id, e.g. "platform-agents" */
  pageId: string;
  icon: LucideIcon;
  /** "brand" (blue) or "accent" (violet) — sets the page's contrasting accent */
  accent: "brand" | "accent";
  graphic: GraphicName;
  name: string;
  eyebrow: string;
  headline: string;
  subhead: string;
  /** the "why this matters" lead paragraph */
  intro: string;
  /** distinct feature sections rendered in alternating bands */
  blocks: FeatureBlock[];
  howHeadline?: string;
  how?: HowStep[];
  /** capability-framed chips shown under how-it-works */
  capabilities?: string[];
  /** extra card sections, rendered after the feature blocks */
  cardGroups?: CardGroup[];
  /** Integrations page: the connector catalogue (from ariviti.com/integrations) */
  connectorGroups?: ConnectorGroup[];
  /** Domain Packs page: live and in-design packs */
  packs?: PackItem[];
  /** Edge AI page: supported small models */
  models?: ModelRow[];
  outcomes?: Outcome[];
  comparisons?: Comparison[];
  faq?: { q: string; a: string }[];
  related: RelatedLink[];
  metaTitle: string;
  metaDescription: string;
}

export const platformTopics: PlatformTopic[] = [
  {
    slug: "why",
    pageId: "platform-why",
    icon: Compass,
    accent: "brand",
    graphic: "workflow",
    name: "Why TurfAI",
    eyebrow: "Why TurfAI",
    headline: "Not a model wrapper. The rails around the model.",
    subhead:
      "Most teams don't fail at AI because the model is weak. They fail at identity, audit, integration, and the jump from demo to production. TurfAI is the orchestration layer built for that jump: agents that act under governance, on your stack, with a trail you can defend.",
    intro:
      "Calling a model is the easy part. Turning that call into work you can trust in production means identity, memory, execution, messaging, storage, and observability — plus clear ownership and an audit trail. That infrastructure is what TurfAI is.",
    blocks: [
      {
        title: "The bottleneck is never the model",
        body:
          "POCs stall for reasons that have nothing to do with model quality: AI systems that never connect to real operations, no clear owner once the project ends, and users who don't trust an answer they can't verify.",
        points: [
          "Disconnected from the systems where work actually happens",
          "No governance, so no one will sign off on production",
          "Opaque outputs users won't act on",
        ],
      },
      {
        title: "TurfAI supplies the rails",
        body:
          "A visual workflow engine, governed integrations, human-in-the-loop gates, and an immutable audit trail — the production infrastructure that turns a promising demo into an operational system.",
        points: [
          "One auditable execution layer from trigger to action",
          "Human checkpoints exactly where judgment is required",
          "Every step logged, costed, and attributable",
        ],
      },
    ],
    cardGroups: [
      {
        eyebrow: "What actually makes TurfAI different",
        headline: "Six reasons, not a feature list.",
        items: [
          { title: "Data Shield", body: "Personal data is tokenised before it reaches a model, and the reversal key lives in your KMS — not ours, not the LLM provider's. Every run leaves an immutable audit trail you can hand to a regulator." },
          { title: "Bidirectional MCP", body: "TurfAI is a Model Context Protocol server today — 19 tools and 3 resource types your existing AI clients can call — not just a consumer of someone else's tools." },
          { title: "Agent Squads", body: "Teams of specialised agents that share results on a blackboard and run in sequence or under a manager agent — not one prompt trying to do everything." },
          { title: "Agentic, not scripted", body: "A reason–act–observe loop with goal decomposition and dynamic tool selection, with human-in-the-loop and per-agent guardrails where you set them." },
          { title: "LLM-agnostic", body: "Claude, GPT, Gemini, or open models such as Llama and Mistral self-hosted through Ollama. Swap models without rebuilding the workflow." },
          { title: "Portable Domain Packs", body: "Agents, workflows, and squads ship as portable JSON: on-prem-ready, exportable, and yours to extend." },
        ],
      },
    ],
    comparisons: [
      { vs: "Generic automation", body: "Workflow tools move data between apps. They were not built for non-deterministic agents that need bounded autonomy and human-in-the-loop control.", tag: "Built for agents, with a person approving the steps that carry risk." },
      { vs: "Open-source builders", body: "Open builders are powerful for developers. TurfAI adds what an enterprise needs on top — Data Shield, full audit, governed prompts, and managed deployment — instead of leaving you to assemble it.", tag: "Governance and security built in, not bolted on." },
      { vs: "Building in-house", body: "Building your own orchestration layer — identity, audit, observability, deployment — takes a dedicated platform team and several quarters. TurfAI gives you production-grade rails from day one.", tag: "Production in 3–4 weeks, not a multi-quarter build." },
    ],
    outcomes: [
      { value: "11×", label: "Clinical throughput", note: "Healthcare · in production" },
      { value: "99.4%", label: "Accuracy", note: "Healthcare · chart audit" },
      { value: "24×", label: "Employee productivity", note: "BFSI · in production" },
      { value: "10×", label: "HR workflow efficiency", note: "HR Tech · in production" },
    ],
    faq: [
      { q: "Is this just a model wrapper?", a: "No. It is the rails around the model: orchestration, identity, Data Shield, memory, execution, messaging, storage, and observability." },
      { q: "Do we get locked into one model?", a: "No. TurfAI is LLM-agnostic — Claude, GPT, Gemini, or open models such as Llama and Mistral self-hosted through Ollama — and you swap models without rebuilding the workflow." },
      { q: "Can we run it on our own infrastructure?", a: "Yes. TurfAI runs as managed SaaS, in your own AWS, Azure or GCP account (BYOC), on-prem inside your perimeter, or at the edge on Jetson Orin NX for latency-sensitive and air-gapped sites." },
    ],
    related: [
      { page: "platform-agents", label: "AI Agents", desc: "Reasoning agents that call governed tools." },
      { page: "platform-domain-packs", label: "Domain Packs", desc: "Production-tuned starting points, not pilots." },
      { page: "security", label: "Security & Trust", desc: "RBAC, Data Shield, and audit by default." },
    ],
    metaTitle: "Why TurfAI — The rails around the model | TurfAI",
    metaDescription:
      "Enterprise AI POCs stall on infrastructure, not models. TurfAI supplies the execution layer — orchestration, governance, HITL, and audit — that gets AI into production.",
  },
  {
    slug: "agents",
    pageId: "platform-agents",
    icon: Bot,
    accent: "accent",
    graphic: "agents",
    name: "AI Agents",
    eyebrow: "AI Agents",
    headline: "Agents that survive contact with production.",
    subhead:
      "Most agent demos work once, on a clean input, with a human watching. TurfAI runs multi-step agents the way your business runs them — with identity, memory, approval gates, and a full execution trail.",
    intro:
      "An agent is only useful in the enterprise if you can see what it did and constrain what it can touch. TurfAI agents operate against a governed tool registry, so autonomy never means loss of control.",
    blocks: [
      {
        title: "A governed tool registry",
        body:
          "Agents can only call the tools you grant them. Register REST APIs, email, Drive, and MCP servers as governed tools, scoped per agent and logged on every call.",
        points: [
          "Reason → act → observe loop on a visual canvas",
          "MCP client and server integration for external tools",
          "Every tool call captured in the audit log",
        ],
      },
      {
        title: "Prompt Lab and multi-model",
        body:
          "Design and test prompts before you ship. Prompt Lab runs them against OpenAI, Anthropic and Google Vertex models side by side and checks structured JSON outputs against a schema you define; in production, route different steps to different models, including self-hosted ones through Ollama.",
        points: [
          "Interactive prompt testing with structured output schemas",
          "Multi-model support — pick the right model per task",
          "Classification, extraction, summarisation, and free-form tasks",
        ],
      },
    ],
    cardGroups: [
      {
        eyebrow: "What we run",
        headline: "Agent patterns we run in production.",
        sub: "Three patterns, not a feature catalogue — the ones we put in front of regulated enterprises.",
        items: [
          { title: "Multi-agent pipelines", body: "Specialised agents hand work to each other — extract, decide, act, verify — instead of one prompt trying to do everything." },
          { title: "Human-in-the-loop agents", body: "Approval gates on the steps that carry risk. The agent proposes; a named human signs off; the decision is logged." },
          { title: "Document & decision agents", body: "Read messy enterprise documents, extract what matters, and route the decision — the work behind a B2B lender's 24× and a healthcare chart audit." },
        ],
      },
      {
        eyebrow: "Why agents reach production here",
        headline: "Built for the part after the demo.",
        items: [
          { title: "Bounded autonomy", body: "You decide where the agent acts on its own and where it must ask. Autonomy is a setting, not a leap of faith." },
          { title: "Cost and token control", body: "Per-step model routing and budget ceilings, so a runaway loop is a config limit, not a surprise invoice." },
          { title: "Memory that persists", body: "State that survives across steps and sessions — agents that remember the case, not just the last message." },
          { title: "Observability built in", body: "Execution traces, audit logs, and metrics from day one: what the agent saw, what it decided, who approved, what it changed." },
        ],
      },
    ],
    howHeadline: "How a TurfAI agent is built",
    how: [
      { title: "Connect", desc: "REST API nodes, MCP servers, and enterprise connectors give the agent the systems it needs." },
      { title: "Orchestrate", desc: "Multi-step pipelines with memory, retries, approval gates, and human-in-the-loop control." },
      { title: "Observe", desc: "Every run is traceable: what it saw, what it decided, who approved, what it changed." },
    ],
    faq: [
      { q: "Which models can agents use?", a: "Bring your own. TurfAI orchestrates around the model and doesn't lock you to one — route different steps to different models." },
      { q: "How do you stop an agent from doing something wrong?", a: "Approval gates and bounded autonomy. High-risk steps require a named human sign-off, and every action is logged." },
      { q: "Can agents run on our own infrastructure?", a: "Yes. TurfAI runs as managed SaaS, in your own AWS, Azure or GCP account (BYOC), on-prem inside your perimeter, or at the edge on Jetson Orin NX for latency-sensitive and air-gapped sites." },
    ],
    related: [
      { page: "platform-squads", label: "Agent Squads", desc: "Coordinate specialists on complex work." },
      { page: "platform-integrations", label: "Integrations & MCP", desc: "The tools agents are allowed to call." },
      { page: "platform-rag", label: "RAG & Knowledge", desc: "Ground agent answers in your documents." },
    ],
    metaTitle: "AI Agents — Agents that survive production | TurfAI",
    metaDescription:
      "TurfAI runs multi-step agents with identity, memory, approval gates, and a full execution trail — multi-agent pipelines, human-in-the-loop agents, and document & decision agents.",
  },
  {
    slug: "squads",
    pageId: "platform-squads",
    icon: Users,
    accent: "accent",
    graphic: "squads",
    name: "Agent Squads",
    eyebrow: "Agent Squads",
    headline: "Some work is too complex for one agent. Use a team.",
    subhead:
      "Multi-agent squads coordinate specialised agents toward a shared goal — with a defined composition, a coordination pattern, and a clear human sign-off at the end.",
    intro:
      "Real processes — settling a claim, underwriting a submission — need many steps and many skills. A single agent can't hold all of it reliably. A squad splits the work across specialists that collaborate.",
    blocks: [
      {
        title: "Composition and coordination",
        body:
          "Assemble specialist agents, each with its own goal and tools, then choose how they run: sequentially, each task building on the last, or hierarchically, with a manager agent delegating work to the team. Either way, agents share results on a blackboard.",
        points: [
          "Defined composition and coordination per squad",
          "A governed tool registry shared across the squad",
          "Sequential or hierarchical (manager-led) execution",
        ],
      },
      {
        title: "A human at the end",
        body:
          "A squad hands back a draft — a settlement offer, a recommendation memo — and a person reviews and approves before anything is final. Automation carries the volume; people own the judgment.",
        points: [
          "Clear human hand-off point on every squad",
          "Draft-then-approve keeps accountability with people",
          "Full audit trail across every agent in the squad",
        ],
      },
    ],
    howHeadline: "How a squad works",
    how: [
      { title: "Compose", desc: "Assemble specialist agents, each with its own goal and tools." },
      { title: "Coordinate", desc: "The squad runs in sequence, or a manager agent delegates the work." },
      { title: "Produce", desc: "It hands back a draft: a settlement offer, a memo." },
      { title: "Sign off", desc: "A person reviews and approves before anything is final." },
    ],
    related: [
      { page: "platform-agents", label: "AI Agents", desc: "The specialists a squad is built from." },
      { page: "solutions-bfsi", label: "BFSI", desc: "Squads that draft a credit memo." },
      { page: "security", label: "Security & Trust", desc: "Audit trails across every agent in the squad." },
    ],
    metaTitle: "Agent Squads — Multi-agent coordination | TurfAI",
    metaDescription:
      "TurfAI agent squads coordinate specialised agents on complex work with a defined composition, a coordination pattern, a shared governed tool registry, and human sign-off.",
  },
  {
    slug: "rag",
    pageId: "platform-rag",
    icon: Database,
    accent: "brand",
    graphic: "rag",
    name: "RAG & Knowledge",
    eyebrow: "RAG & Knowledge",
    headline: "Turn your documents into answers your team can trust.",
    subhead:
      "Index your knowledge, ask in natural language, and get responses that cite exactly where they came from — the retrieval layer behind support agents and squads.",
    intro:
      "Nobody acts on an answer they can't verify. TurfAI grounds responses in your own documents and shows the sources, so every answer comes with its receipts.",
    blocks: [
      {
        title: "Grounded, cited retrieval",
        body:
          "Documents are embedded for semantic search. Questions run over that index, and every answer references the source documents it used.",
        points: [
          "Semantic search over your own documents (pgvector)",
          "Source citations on every answer",
          "Persistent sessions keep context across a conversation",
        ],
      },
      {
        title: "Powers agents and squads",
        body:
          "RAG isn't a silo — it's the knowledge layer other capabilities build on. Support agents answer from it, and squads use it for lookups mid-workflow.",
        points: [
          "Natural-language Q&A with full conversation history",
          "Feeds grounded context into agents and squads",
          "Answers stay tied to the source of truth",
        ],
      },
    ],
    howHeadline: "Retrieval that shows its work",
    how: [
      { title: "Index", desc: "Documents are embedded for semantic search." },
      { title: "Ask", desc: "Natural-language questions run over the knowledge base." },
      { title: "Cite", desc: "Every answer references the source documents it used." },
      { title: "Continue", desc: "Persistent sessions keep context across the conversation." },
    ],
    related: [
      { page: "platform-agents", label: "AI Agents", desc: "Agents that retrieve before they answer." },
      { page: "solutions-it-ops", label: "IT Ops", desc: "Answers grounded in your runbooks." },
      { page: "solutions-healthcare", label: "Healthcare", desc: "Grounded answers over policies and records." },
    ],
    metaTitle: "RAG & Knowledge — Grounded answers with citations | TurfAI",
    metaDescription:
      "TurfAI indexes your documents for semantic search and answers in natural language with source citations — the retrieval layer behind support agents and agent squads.",
  },
  {
    slug: "chatbots",
    pageId: "platform-chatbots",
    icon: MessageSquare,
    accent: "brand",
    graphic: "rag",
    name: "Chatbots",
    eyebrow: "Chatbots & Knowledge Base",
    headline: "A cited assistant on your site, without the made-up answers.",
    subhead:
      "Build a chatbot over your own knowledge base, embed it with one snippet, and keep every conversation, key and source under your control.",
    intro:
      "A support bot that invents answers costs more than no bot. TurfAI chatbots answer only from the collections you give them, cite the source of every reply, and say so when the answer isn't in the knowledge base.",
    blocks: [
      {
        title: "Built on your knowledge base",
        body:
          "Choose the document collections a chatbot may draw on. Upload files, pull from Google Drive or ingest web pages; TurfAI indexes them and every reply cites the documents it used.",
        points: [
          "Answers grounded in the collections you choose",
          "Source citations on every reply",
          "Uploads, Drive files and web pages as sources",
        ],
      },
      {
        title: "Embed it anywhere",
        body:
          "Copy a script tag, a sandboxed iframe or a REST call from the chatbot's settings. The widget runs on your site; the public chat API serves your own apps.",
        points: [
          "Three embed formats, generated for you",
          "Per-origin rate limits",
          "Public chat API for custom front ends",
        ],
      },
      {
        title: "Keys, transcripts and deletion under control",
        body:
          "API keys are hashed at rest and shown once; keys older than 90 days are flagged for rotation. Owners review transcripts with citations intact, delete any conversation, or switch history off.",
        points: [
          "One-time key reveal and rotation tracking",
          "Conversation transcripts with sources",
          "Per-conversation delete, or no history at all",
        ],
      },
    ],
    howHeadline: "From documents to a live chatbot",
    how: [
      { title: "Collect", desc: "Add documents, Drive folders or web pages to a collection." },
      { title: "Configure", desc: "Choose its collections, model and persona, and who can reach it." },
      { title: "Embed", desc: "Paste the snippet into your site, or call the API." },
      { title: "Review", desc: "Read transcripts and tune the knowledge base." },
    ],
    cardGroups: [
      {
        eyebrow: "Where teams use it",
        headline: "One engine, three front doors.",
        items: [
          { title: "Customer support", body: "Answers from your product docs and policies, with escalation to your team built into the workflow behind it. Ships with the Customer Support Domain Pack." },
          { title: "Employee help desk", body: "HR and IT questions answered from your own policies. Ask HR is built on exactly this." },
          { title: "Docs assistant", body: "A cited assistant on your developer or product documentation, so readers find the page instead of filing a ticket." },
        ],
      },
    ],
    faq: [
      { q: "Will it answer from the open internet?", a: "No. A chatbot answers only from the collections you attach to it, and cites them." },
      { q: "Can we keep conversation history out of the platform?", a: "Yes. History is a per-chatbot setting; when it is off, conversations are not stored." },
      { q: "How do we put it on our site?", a: "Copy one of the three generated snippets — script tag, sandboxed iframe or REST — from the chatbot's settings." },
    ],
    related: [
      { page: "platform-rag", label: "RAG & Knowledge", desc: "The retrieval layer every chatbot runs on." },
      { page: "platform-governance", label: "Governance & Controls", desc: "Keys, caps and audit for every chatbot." },
      { page: "solutions-hr-tech", label: "HR Tech", desc: "Ask HR, an employee help desk built on chatbots." },
    ],
    metaTitle: "Chatbots — Cited answers, embedded anywhere | TurfAI",
    metaDescription:
      "Build TurfAI chatbots over your own knowledge base, embed them with a script tag, iframe or API, and keep keys, transcripts and deletion under your control.",
  },
  {
    slug: "integrations",
    pageId: "platform-integrations",
    icon: Plug,
    accent: "brand",
    graphic: "integrations",
    name: "Integrations & MCP",
    eyebrow: "Integrations & MCP",
    headline: "Wire AI into the stack you already run.",
    subhead:
      "TurfAI connects through native processors, a config-only OAuth2 adapter, and a bidirectional MCP hub — plus any authenticated REST API. Deep on the systems that matter to your workflow, not shallow on a thousand.",
    intro:
      "An agent is only as useful as the systems it can reach. TurfAI connects to your stack through governed integrations and the Model Context Protocol, so agents act on real systems without anyone hand-rolling a connector layer.",
    blocks: [
      {
        title: "Triggers in",
        body:
          "Start a workflow however the work arrives — an inbound webhook, a scheduled run, or a file drop. Each webhook ships an auto-generated curl sample from its schema.",
        points: [
          "Webhook triggers accepting JSON, form-data, and file uploads",
          "Cron-based scheduled runs, Google Drive folder watches, and Gmail inbox triggers",
          "Signed webhooks with origin pinning and secret rotation",
          "Auto-generated curl samples from every webhook schema",
        ],
      },
      {
        title: "Actions out, via MCP",
        body:
          "Act on your systems through governed tools: send email, fetch from Drive, call any REST API, or expose and consume tools over MCP — with credentials held securely per user.",
        points: [
          "Email, Gmail, Google Drive, SharePoint and JobAdder tasks, plus generic REST",
          "MCP client and server for exposing and consuming tools",
          "Secure, per-user storage of integration credentials",
        ],
      },
    ],
    cardGroups: [
      {
        eyebrow: "How it connects",
        headline: "Three ways to connect.",
        items: [
          { title: "Native processors & REST", body: "Built-in processors for Google Drive, Gmail, SharePoint, JobAdder, email, DOCX and PDF output, and web scraping — plus a universal REST API node and inbound webhooks. If it has an API, TurfAI calls it." },
          { title: "OAuth2 apps", body: "A config-only OAuth2 adapter with Microsoft 365, Salesforce, and Slack seeded. Adding a provider is a config entry, not a code change." },
          { title: "MCP, both directions", body: "TurfAI is a Model Context Protocol server today — 19 tools and 3 resource types your AI clients can call — and ships eight pre-built MCP connectors to bring external tools in." },
        ],
      },
    ],
    connectorGroups: [
      { tier: "Native", items: [
        { name: "Google Drive", desc: "Read, write, and watch Drive files via OAuth2 or a service account." },
        { name: "Gmail", desc: "Fetch, filter, reply, and handle attachments — or start a workflow when mail arrives." },
        { name: "SharePoint", desc: "Read and summarise documents from SharePoint sites and libraries." },
        { name: "JobAdder", desc: "Pull jobs and candidates from the JobAdder ATS into hiring workflows." },
        { name: "Email / SMTP", desc: "Send templated HTML email through any SMTP provider." },
        { name: "REST API", desc: "Call any authenticated HTTP endpoint as an observable workflow step." },
        { name: "Document Generation", desc: "Generate DOCX documents from templates." },
        { name: "PDF output", desc: "Render text and reports to PDF as a workflow step." },
        { name: "Web Scraping", desc: "Extract page content and metadata from the open web." },
        { name: "Webhooks", desc: "Trigger workflows from any external system on inbound events." },
      ] },
      { tier: "OAuth2 app", items: [
        { name: "Microsoft 365", desc: "OAuth2 app access to Microsoft 365." },
        { name: "Salesforce", desc: "OAuth2 app access to Salesforce records and objects." },
        { name: "Slack", desc: "OAuth2 app access to Slack messaging and channels." },
      ] },
      { tier: "MCP connector", items: [
        { name: "Company Database", desc: "Typed, governed access to your company database." },
        { name: "GitHub", desc: "Read repositories and automate change actions." },
        { name: "Slack (MCP)", desc: "Bring Slack context and actions to agents." },
        { name: "Brave Search", desc: "Live web search as a governed tool." },
        { name: "Postgres Analytics", desc: "Query governed Postgres data through a typed connector." },
        { name: "Google Maps", desc: "Geocoding and places data as a tool." },
        { name: "File System", desc: "Scoped file read and write access." },
        { name: "Memory / State", desc: "Persistent memory and state for agents." },
      ] },
      { tier: "Commerce", items: [
        { name: "Shopify", desc: "Install TurfAI from the Shopify admin and switch on agents for order tracking, returns, payment follow-ups and customer win-back — triggered by store events, answering from your store's policies." },
      ] },
      { tier: "Template", items: [
        { name: "HubSpot", desc: "Runs on the REST API task plus a workflow template." },
        { name: "Jira", desc: "Open, update, and resolve issues over REST." },
        { name: "Twilio SMS", desc: "Send SMS and alerts over REST." },
        { name: "Stripe", desc: "Reconcile payments over REST." },
      ] },
      { tier: "Channel", items: [
        { name: "WhatsApp", desc: "Agent channel via the Meta Cloud API or Vonage." },
        { name: "SMS", desc: "Two-way SMS through Vonage." },
        { name: "Web widget", desc: "Embeddable chat widget for any site or app." },
        { name: "Public chat API", desc: "Reach agents programmatically over a public chat API." },
      ] },
      { tier: "Models", items: [
        { name: "OpenAI", desc: "GPT models for agents, extraction and chat." },
        { name: "Anthropic", desc: "Claude models for agents, extraction and chat." },
        { name: "Google Vertex AI", desc: "Gemini models, embeddings and Vertex endpoints." },
        { name: "Ollama", desc: "Self-hosted open models — Llama, Mistral, Qwen, Phi and more." },
      ] },
      { tier: "Runtime & data", items: [
        { name: "Elastic", desc: "Hybrid search and vector retrieval — our Centre of Excellence." },
        { name: "Snowflake", desc: "Governed enterprise data for retrieval and analytics." },
        { name: "AWS", desc: "Deploy across AWS services and BYOC environments." },
        { name: "Azure", desc: "Deploy across Microsoft Azure services." },
        { name: "GCP", desc: "Deploy across Google Cloud services." },
        { name: "Red Hat", desc: "OpenShift and the platform layer for on-prem and hybrid." },
      ] },
    ],
    related: [
      { page: "platform-agents", label: "AI Agents", desc: "The agents that call these tools." },
      { page: "security", label: "Security & Trust", desc: "How credentials and access are governed." },
      { page: "platform-domain-packs", label: "Domain Packs", desc: "Pre-wired integrations by domain." },
    ],
    metaTitle: "Integrations & MCP — Wire AI into your stack | TurfAI",
    metaDescription:
      "TurfAI connects through native processors, a config-only OAuth2 adapter (Microsoft 365, Salesforce, Slack), and a bidirectional MCP hub — plus any authenticated REST API.",
  },
  {
    slug: "governance",
    pageId: "platform-governance",
    icon: Gauge,
    accent: "brand",
    graphic: "shield",
    name: "Governance & Controls",
    eyebrow: "Governance & Controls",
    headline: "Spend caps, prompt review and audit trails, before you need them.",
    subhead:
      "The controls that let a platform team say yes to AI: who can publish a prompt, what a workflow may spend, what happens when it fails, and a record of all of it.",
    intro:
      "Production AI stalls on governance long before it stalls on models. TurfAI ships those controls as product features your admins use, not as a policy document.",
    blocks: [
      {
        title: "Prompts are reviewed, then published",
        body:
          "Prompts live in a versioned library and must be published before a workflow can use them. Publishing runs an injection check, and untrusted inputs — webhook bodies, agent outputs — are marked before the model sees them.",
        points: [
          "Versioned prompt library with a review queue",
          "Injection check at publish time",
          "Prompt Lab to test prompts against a JSON schema",
        ],
      },
      {
        title: "Spend you can cap",
        body:
          "Set monthly token and cost caps per tenant or per workflow. Owners get a warning at 80%; new runs stop at 100%, and a run that crosses the cap mid-flight is stopped.",
        points: [
          "Token and cost caps per tenant or workflow",
          "80% warnings by email",
          "Live cost view by workflow, with admin drill-down",
        ],
      },
      {
        title: "Failures you can see and replay",
        body:
          "Every failure is classified as transient, terminal or user error, with a normalised error code. Retry rules, a time limit per workflow and a fallback chain are set per workflow or tenant-wide; failed jobs can be replayed in one click.",
        points: [
          "Classified failures with error codes",
          "Retry rules and replay of failed jobs",
          "Per-workflow time limits and model fallbacks",
        ],
      },
      {
        title: "A record of everything",
        body:
          "Runs, sign-ins, cap changes and Data Shield events go to an immutable audit log, with personal data scrubbed from log entries. Users see their own activity; data export and verified deletion are self-serve.",
        points: [
          "Immutable audit log, personal data scrubbed",
          "Account activity and failures views",
          "Signed data export and verified deletion with a 7-day grace period",
        ],
      },
    ],
    capabilities: [
      "Signed webhooks (HMAC-SHA256)",
      "Webhook origin pinning",
      "Secret rotation with a 24-hour grace window",
      "Require Data Shield on every LLM step",
      "Validation before publish",
      "Tenant-wide resilience defaults",
    ],
    faq: [
      { q: "Can one workflow run up a large model bill?", a: "Not past its cap. Caps are checked before a run starts and during it; a run that crosses its cap is stopped." },
      { q: "Who can change a prompt in production?", a: "Only a published prompt version can run. Changes go through the review queue and the publish-time injection check." },
      { q: "Can we export or delete everything you hold for us?", a: "Yes, from account settings: a signed export bundle, or a verified deletion with a 7-day window to cancel." },
    ],
    related: [
      { page: "security", label: "Security & Trust", desc: "Data Shield and OWASP GenAI coverage." },
      { page: "platform-chatbots", label: "Chatbots", desc: "Keys, transcripts and deletion per chatbot." },
      { page: "platform-agents", label: "AI Agents", desc: "Agents that act only through granted tools." },
    ],
    metaTitle: "Governance & Controls — Caps, prompt review, audit | TurfAI",
    metaDescription:
      "TurfAI governance: versioned prompts with publish-time injection checks, token and cost caps per workflow, classified failures with replay, and an immutable audit log.",
  },
  {
    slug: "edge-ai",
    pageId: "platform-edge-ai",
    icon: Cpu,
    accent: "brand",
    graphic: "workflow",
    name: "Edge AI",
    eyebrow: "Edge AI",
    headline: "Run AI where your data lives — on small models you control.",
    subhead:
      "TurfAI runs the same workflows on open small language models inside your perimeter — on your GPU servers or on NVIDIA Jetson Orin NX at the edge — so documents never leave, costs stay flat and you choose the model.",
    intro:
      "Not every step needs a frontier model. Routine extraction and summaries run well on small models you host yourself — and when they run inside your walls, regulated documents never leave.",
    blocks: [
      {
        title: "Same workflows, your models",
        body:
          "Workflows, agents and Data Shield run unchanged; only the model behind each step changes, through TurfAI's Ollama and vLLM support. Quantised builds (8-bit and 4-bit) fit larger models on smaller hardware.",
        points: [
          "Ollama for simplicity, vLLM for throughput",
          "8-bit and 4-bit quantised models",
          "Small models for routine steps, a frontier model only where it earns its cost",
        ],
      },
      {
        title: "Measured, not assumed",
        body:
          "Every candidate model is benchmarked on your own documents — field-level accuracy, P95 latency, throughput and GPU power — against a cloud baseline before you switch.",
        points: [
          "Accuracy scored field by field against a hand-checked set",
          "Throughput at 1, 2, 4 and 8 parallel requests",
          "GPU use, memory, power and temperature per run",
        ],
      },
      {
        title: "Text and vision, private by design",
        body:
          "Vision models extract fields from scanned IDs and images; text models write summaries by document type. KYC and other regulated documents are processed with no external API calls.",
        points: [
          "KYC extraction from Aadhaar, PAN, passport and voter ID",
          "Summaries for healthcare, RFP, invoice, resume and research documents",
          "On GPU servers or NVIDIA Jetson Orin NX at the edge",
        ],
      },
    ],
    models: [
      { family: "Microsoft Phi-4", sizes: "3.8B (mini), 14B", kind: "Text", tested: "Phi-4-mini" },
      { family: "Meta Llama 3.1 / 3.2", sizes: "1B, 3B, 8B; 11B Vision", kind: "Text, vision", tested: "Llama 3.2 3B (incl. 8-bit)" },
      { family: "Alibaba Qwen2.5 / Qwen3", sizes: "0.5B–32B", kind: "Text", tested: "Qwen2.5 7B" },
      { family: "Alibaba Qwen2.5-VL", sizes: "3B, 7B, 32B", kind: "Vision", tested: "Qwen2.5-VL 7B (incl. 4-bit)" },
      { family: "Google Gemma 3", sizes: "1B, 4B, 12B, 27B", kind: "Text, vision", tested: "Gemma 3 4B" },
      { family: "Mistral 7B / Mistral Small", sizes: "7B, 24B", kind: "Text", tested: "—" },
      { family: "DeepSeek-R1 distilled", sizes: "1.5B–32B", kind: "Reasoning", tested: "—" },
      { family: "MiniCPM-V", sizes: "8B", kind: "Vision", tested: "MiniCPM-V" },
      { family: "IBM Granite 3", sizes: "2B, 8B", kind: "Text", tested: "—" },
    ],
    faq: [
      { q: "Do we lose accuracy with a small model?", a: "Sometimes, on some steps. That's why every model is benchmarked on your own documents before you switch — and why TurfAI lets you mix small and frontier models step by step." },
      { q: "Which hardware do we need?", a: "A single GPU server runs models up to about 14B comfortably; quantised builds go further. For on-site use, the same workflows run on NVIDIA Jetson Orin NX." },
      { q: "Does Data Shield still apply?", a: "Yes. Workflows, agents and Data Shield run unchanged — only the model behind each step changes." },
    ],
    related: [
      { page: "security", label: "Security & Trust", desc: "Deployment isolation and Data Shield." },
      { page: "promptbench", label: "PromptBench", desc: "Compare models on your own test cases." },
      { page: "platform-agents", label: "AI Agents", desc: "The agents that run on these models." },
    ],
    metaTitle: "Edge AI — Small language models inside your perimeter | TurfAI",
    metaDescription:
      "Run TurfAI workflows on open small language models — Phi-4, Llama 3, Qwen, Gemma, Mistral and more under 36B — on your GPU servers or NVIDIA Jetson Orin NX at the edge.",
  },
  {
    slug: "domain-packs",
    pageId: "platform-domain-packs",
    icon: Boxes,
    accent: "accent",
    graphic: "packs",
    name: "Domain Packs",
    eyebrow: "Domain Packs",
    headline: "Don't start from a blank canvas. Start from production.",
    subhead:
      "Domain Packs are pre-built, tuned TurfAI workflows for a specific industry — the connectors, agents, approval gates, and guardrails already wired, based on deployments that are live today. You configure; you don't rebuild.",
    intro:
      "Every pack is pure configuration on the same platform: the agents, workflows, and human checkpoints a domain needs, ready to run and yours to change.",
    blocks: [
      {
        title: "Deploy as-is or fork",
        body:
          "Install a pack and run it, or clone-to-edit any workflow and agent inside it. Agents, workflows, and squads ship as portable JSON — exportable, and yours to extend from day one.",
        points: [
          "Domain-tuned agents, workflows, and squads",
          "Configurable human-in-the-loop at every critical step",
          "Deploys wherever TurfAI runs — SaaS, BYOC, on-prem, or edge",
        ],
      },
    ],
    cardGroups: [
      {
        eyebrow: "What's inside",
        headline: "What's inside every pack.",
        items: [
          { title: "Pre-wired workflows", body: "The agents, steps, and connectors for the job — assembled, not described in a slide." },
          { title: "Guardrails on by default", body: "Human-in-the-loop gates and audit logging configured for the industry's risk profile from day one." },
          { title: "Tuned, then transferred", body: "We tune the pack to your data and process, prove the outcome in production, and hand you the controls." },
        ],
      },
    ],
    packs: [
      { name: "Healthcare", status: "Live", desc: "Chart audit, prior authorisation, and clinical documentation.", page: "solutions-healthcare" },
      { name: "Insurance", status: "Live", desc: "Claims triage and settlement, underwriting, renewals, fraud indicators, and complaints.", page: "solutions-insurance" },
      { name: "IT Ops", status: "Live", desc: "Alert correlation, root cause analysis, auto-remediation, and escalation with context.", page: "solutions-it-ops" },
      { name: "BFSI", status: "Live", desc: "Loan document ingestion, KYC extraction, and credit memos.", page: "solutions-bfsi" },
      { name: "HR Tech", status: "Live", desc: "HR support triage and hiring orchestration.", page: "solutions-hr-tech" },
      { name: "Customer Support", status: "Live", desc: "Cited answers, ticket triage, and hand-off to an agent with context.", page: "platform-chatbots" },
      { name: "Education & Learning", status: "Live", desc: "Course content generation, adaptive assessment, and training ROI." },
      { name: "Project Management", status: "Live", desc: "Status reports, risk and issue tracking, and sprint planning." },
      { name: "Legal", status: "Live", desc: "Petition replies, NDAs, case timelines and litigation reports.", page: "solutions-legal" },
      { name: "FinTech", status: "In design", desc: "Onboarding, KYC packs, and statement processing." },
    ],
    related: [
      { page: "platform-agents", label: "AI Agents", desc: "The agents every pack is built from." },
      { page: "platform-integrations", label: "Integrations & MCP", desc: "The connectors packs come pre-wired with." },
      { page: "solutions", label: "Solutions", desc: "The industries the packs serve." },
    ],
    metaTitle: "Domain Packs — Start from production | TurfAI",
    metaDescription:
      "TurfAI Domain Packs are pre-built, tuned workflows by industry. Nine live today — Healthcare, Insurance, IT Ops, BFSI, HR Tech, Customer Support, Education & Learning, Project Management and Legal — and FinTech in design.",
  },
];

export function getPlatformTopic(slug: string): PlatformTopic | undefined {
  return platformTopics.find((t) => t.slug === slug);
}
